import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector, useAppStore } from "../redux/hooks";
import {
  addViewedJob,
  removeViewedJob,
  addAppliedJob,
  removeAppliedJob,
  addSavedJob,
  removeSavedJob,
  updateJobNotes,
  updateJobTags,
  updateJobChecklist,
  isChecklistItem,
  type Job,
  type ApplicationState,
  type ChecklistItem,
} from "../redux/features/jobs/jobsSlice";
import {
  useDeleteUserStateItemMutation,
  useUpdateUserStateItemMutation,
  userStateApi,
} from "../redux/services/userStateApi";
import type { UserStateEntry, UserStateItemKey } from "../types/user-state";
import { setEntry } from "../redux/features/userState/userStateSlice";
import { useAuth } from "./useAuth";
import { createExternalAppliedJobId } from "../utils/job-tracking/external-applied-job-id";
import { normalizeExternalJobUrl } from "../utils/job-tracking/normalize-external-job-url";
import type { ExternalJobUrlNormalizeFailureReason } from "../types/external-applied-job";
import { resolveTrackedJobRef, timestampExtrasFromJob } from "../utils/job-tracking/resolve-tracked-job-ref";
import { buildJobUserStatePayload as buildJobPayload } from "../utils/user-state/build-job-user-state-payload";

type ConflictAwareError = {
  status?: number;
  data?: unknown;
  error?: {
    status?: number;
    data?: unknown;
  };
};

function ensureJobId(job: Job, jobId: string): Job {
  return { ...job, id: jobId };
}

function normalizeJobId(job: Job): string {
  const id = job.id ?? (job as { objectID?: string }).objectID;
  return id ? String(id) : "";
}

function jobEntryToJob(entry: UserStateEntry | null): Job | null {
  if (!entry || !entry.data) return null;
  const data = entry.data as Record<string, unknown>;
  const id = data.id ?? data.objectID;
  if (!id) return null;
  const job: Job = {
    id: String(id),
    title: data.title,
    normalisedTitle: data.normalisedTitle,
    companyId: data.companyId,
    companyName: typeof data.companyName === 'string' ? data.companyName : undefined,
  } as Job;
  if (data.applicationState !== undefined && ["interviewing", "offer", "rejected", "hired", null].includes(data.applicationState as any)) {
    job.applicationState = data.applicationState as ApplicationState;
  }
  if (data.isHidden === true) {
    job.isHidden = true;
  }
  if (typeof data.externalUrl === "string" && data.externalUrl.length > 0) {
    job.externalUrl = data.externalUrl;
  }
  if (typeof data.applicationDeadline === "string" && data.applicationDeadline.length > 0) {
    job.applicationDeadline = data.applicationDeadline;
  }
  if (typeof data.notes === "string") {
    job.notes = data.notes;
  }
  if (Array.isArray(data.tags)) {
    job.tags = data.tags.filter((t): t is string => typeof t === "string");
  }
  if (Array.isArray(data.checklist)) {
    job.checklist = data.checklist.filter(isChecklistItem);
  }
  if (data.viewedAt) {
    (job as Record<string, unknown>).viewedAt = data.viewedAt;
  }
  if (data.appliedAt) {
    (job as Record<string, unknown>).appliedAt = data.appliedAt;
  }
  return job;
}

function extractConflictEntry(error: unknown): UserStateEntry | null {
  if (!error || typeof error !== "object") {
    return null;
  }

  const conflict = error as ConflictAwareError;
  
  // RTK Query unwrap() error structure: { status: 409, data: UserStateEntry }
  if (conflict.status === 409 && conflict.data && typeof conflict.data === "object") {
    // Check if data has the structure of UserStateEntry
    const data = conflict.data as Record<string, unknown>;
    if (typeof data.version === "number" && typeof data.data === "object") {
      return conflict.data as UserStateEntry;
    }
  }
  
  // Nested error structure: { error: { status: 409, data: UserStateEntry } }
  if (conflict.error && conflict.error.status === 409 && conflict.error.data && typeof conflict.error.data === "object") {
    const data = conflict.error.data as Record<string, unknown>;
    if (typeof data.version === "number" && typeof data.data === "object") {
      return conflict.error.data as UserStateEntry;
    }
  }

  return null;
}

function extractStatusCode(error: unknown): number | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }
  const conflict = error as ConflictAwareError;
  if (typeof conflict.status === "number") {
    return conflict.status;
  }
  if (conflict.error && typeof conflict.error.status === "number") {
    return conflict.error.status;
  }
  return undefined;
}

export function useUserJobStateSync() {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const viewedJobs = useAppSelector((state) => state.jobs.viewedJobs);
  const appliedJobs = useAppSelector((state) => state.jobs.appliedJobs);
  const savedJobs = useAppSelector((state) => state.jobs.savedJobs);
  const userStateEntries = useAppSelector((state) => state.userState.entries);
  const [updateUserStateItem] = useUpdateUserStateItemMutation();
  const [deleteUserStateItem] = useDeleteUserStateItemMutation();
  const { isAuthenticated } = useAuth();

  const viewedJobsRef = useRef(viewedJobs);
  const appliedJobsRef = useRef(appliedJobs);
  const failedSyncItemKeysRef = useRef<Set<UserStateItemKey>>(new Set());

  useEffect(() => {
    viewedJobsRef.current = viewedJobs;
  }, [viewedJobs]);

  useEffect(() => {
    appliedJobsRef.current = appliedJobs;
  }, [appliedJobs]);

  useEffect(() => {
    if (isAuthenticated) {
      failedSyncItemKeysRef.current.clear();
    }
  }, [isAuthenticated]);

  const upsertJobEntry = useCallback(
    async (
      itemKey: UserStateItemKey,
      payload: Record<string, unknown>,
      version: number,
      callbacks: {
        onConflict?: (latest: UserStateEntry) => void;
        onSuccess?: (result: UserStateEntry) => void;
        onError?: () => void;
      }
    ) => {
      if (!isAuthenticated) {
        return;
      }

      if (failedSyncItemKeysRef.current.has(itemKey)) {
        return;
      }

      try {
        const result = await updateUserStateItem({
          itemKey,
          data: payload,
          version,
        }).unwrap();
        if (result) {
          dispatch(setEntry({ itemKey, entry: result }));
          failedSyncItemKeysRef.current.delete(itemKey);
          callbacks.onSuccess?.(result);
        }
      } catch (rawError) {
        const conflict = extractConflictEntry(rawError);
        if (conflict) {
          // Conflict is expected and handled gracefully - update local state with server version
          dispatch(setEntry({ itemKey, entry: conflict }));
          callbacks.onConflict?.(conflict);
          failedSyncItemKeysRef.current.delete(itemKey);
        } else {
          const status = extractStatusCode(rawError);
          // Only log non-conflict errors
          if (status !== 409) {
            console.error(`[user-state] Failed to sync ${itemKey}`, rawError);
          }
          if (status === 401) {
            failedSyncItemKeysRef.current.add(itemKey);
          }
          callbacks.onError?.();
        }
      }
    },
    [dispatch, isAuthenticated, updateUserStateItem]
  );

  const recordViewedJob = useCallback(
    async (job: Job): Promise<boolean> => {
      const jobId = normalizeJobId(job);
      if (!jobId) {
        return false;
      }

      const currentViewed = viewedJobsRef.current;
      if (currentViewed.some((item) => item.id === jobId)) {
        return true; // Already viewed, consider it success
      }

      const enrichedJob = {
        ...ensureJobId(job, jobId),
        viewedAt: new Date().toISOString(),
      };

      // Always update local Redux state (which is persisted via redux-persist)
      dispatch(addViewedJob(enrichedJob));

      // Validate local state was updated
      const state = store.getState();
      const isInViewedJobs = state.jobs.viewedJobs.some((j) => j.id === jobId);
      if (!isInViewedJobs) {
        console.error("[user-state] Failed to record viewed job locally");
        return false;
      }

      // Only sync to remote if authenticated
      if (!isAuthenticated) {
        return true; // Success for local-only operation
      }

      const itemKey = `viewedJob#${jobId}` as const;
      let entry = userStateEntries[itemKey] as UserStateEntry | undefined;

      // If we don't have the entry locally or it's a default entry, try to fetch it first to avoid conflicts
      if (!entry || (entry.version === 0 && entry.updatedAt === null)) {
        try {
          const fetchedEntry = await dispatch(
            userStateApi.endpoints.getUserStateItem.initiate(itemKey)
          ).unwrap();
          // Only use the fetched entry if it actually exists on the server (updatedAt is not null)
          if (fetchedEntry.updatedAt !== null) {
            entry = fetchedEntry;
          }
        } catch (fetchError) {
          // If fetch fails, continue with version 0
          // This is expected for new items
        }
      }

      try {
        await upsertJobEntry(
          itemKey,
          buildJobPayload(enrichedJob, { viewedAt: enrichedJob.viewedAt }),
          entry?.version ?? 0,
          {
            onConflict: (latest) => {
              const serverJob = jobEntryToJob(latest);
              if (serverJob) {
                dispatch(removeViewedJob(jobId));
                dispatch(addViewedJob(serverJob));
              }
            },
            onError: () => {
              dispatch(removeViewedJob(jobId));
            },
            onSuccess: () => {
              // Success - job is synced
            },
          }
        );
        // Validate job is still in viewed jobs after sync
        const updatedState = store.getState();
        const stillViewed = updatedState.jobs.viewedJobs.some((j) => j.id === jobId);
        return stillViewed;
      } catch (error) {
        // If upsertJobEntry throws or fails, validate local state
        const updatedState = store.getState();
        const stillViewed = updatedState.jobs.viewedJobs.some((j) => j.id === jobId);
        return stillViewed; // Return true if still in local state, false otherwise
      }
    },
    [dispatch, store, isAuthenticated, upsertJobEntry, userStateEntries]
  );

  const recordBrowsedJob = useCallback(
    async (job: Job) => {
      const jobId = normalizeJobId(job);
      if (!jobId) {
        return;
      }

      if (!isAuthenticated) {
        return;
      }

      const itemKey = `browsedJob#${jobId}` as const;
      let entry = userStateEntries[itemKey] as UserStateEntry | undefined;

      // If we don't have the entry locally or it's a default entry, try to fetch it first to avoid conflicts
      if (!entry || (entry.version === 0 && entry.updatedAt === null)) {
        try {
          const fetchedEntry = await dispatch(
            userStateApi.endpoints.getUserStateItem.initiate(itemKey)
          ).unwrap();
          // Only use the fetched entry if it actually exists on the server (updatedAt is not null)
          if (fetchedEntry.updatedAt !== null) {
            entry = fetchedEntry;
            // Job already browsed, no need to update
            return;
          }
        } catch (fetchError) {
          // If fetch fails, continue with version 0
          // This is expected for new items
        }
      } else {
        // Job already browsed, no need to update
        return;
      }

      const enrichedJob = {
        ...ensureJobId(job, jobId),
        browsedAt: new Date().toISOString(),
      };

      await upsertJobEntry(
        itemKey,
        buildJobPayload(enrichedJob, { browsedAt: enrichedJob.browsedAt }),
        entry?.version ?? 0,
        {
          onConflict: () => {
            // Conflict means job was already browsed, ignore
          },
          onError: () => {
            // Silently fail for browsed jobs - not critical
          },
        }
      );
    },
    [dispatch, isAuthenticated, upsertJobEntry, userStateEntries]
  );

  const removeViewedJobRemote = useCallback(
    async (jobId: string) => {
      if (!isAuthenticated) {
        return;
      }
      const existing = viewedJobsRef.current.find((job) => job.id === jobId);
      if (!existing) {
        return;
      }

      dispatch(removeViewedJob(jobId));

      try {
        await deleteUserStateItem({
          itemKey: `viewedJob#${jobId}`,
          version: (userStateEntries[`viewedJob#${jobId}`] as UserStateEntry | undefined)?.version,
        }).unwrap();
      } catch (rawError) {
        const conflict = extractConflictEntry(rawError);
        if (conflict) {
          const restored = jobEntryToJob(conflict);
          if (restored) {
            dispatch(addViewedJob(restored));
            return;
          }
        }
        dispatch(addViewedJob(existing));
      }
    },
    [deleteUserStateItem, dispatch, isAuthenticated, userStateEntries]
  );

  const syncApplicationStateToBackend = useCallback(
    async (jobId: string, applicationState?: ApplicationState, job?: Job): Promise<boolean> => {
      // Validate local state first
      const state = store.getState();
      const allJobs = [...state.jobs.savedJobs, ...state.jobs.viewedJobs, ...state.jobs.appliedJobs];
      const jobInState = allJobs.find((j) => j.id === jobId);
      const hasCorrectState = jobInState?.applicationState === applicationState;
      
      if (!hasCorrectState && applicationState !== undefined) {
        console.warn("[user-state] Local state doesn't match expected applicationState");
        return false;
      }

      if (!isAuthenticated) {
        return hasCorrectState; // Return true if local state is correct
      }

      // Get latest Redux state to find job data
      const latestState = store.getState();
      const latestAppliedJobs = latestState.jobs.appliedJobs;
      const latestUserStateEntries = latestState.userState.entries;
      
      // ApplicationState is only stored in appliedJob# entries (mutually exclusive, only applies to applied jobs)
      const appliedItemKey = `appliedJob#${jobId}` as const;
      let appliedEntry = latestUserStateEntries[appliedItemKey] as UserStateEntry | undefined;
      
      // Check if job is actually applied
      const isApplied = latestAppliedJobs.some((item) => item.id === jobId);
      
      // If applicationState was passed explicitly, use it (most reliable)
      // Otherwise, try to get it from the job in Redux
      const stateToSync = applicationState ?? latestAppliedJobs.find((j) => j.id === jobId)?.applicationState;
      
      // If we don't have an applicationState to sync, skip
      if (stateToSync === undefined) {
        return;
      }

      // Get job data from parameter, appliedJob entry, Redux, or other userState entries
      let jobData = job;
      if (!jobData) {
        jobData = latestAppliedJobs.find((j) => j.id === jobId);
      }
      if (!jobData && appliedEntry?.data) {
        const entryData = appliedEntry.data as Record<string, unknown>;
        jobData = {
          id: jobId,
          title: entryData.title,
          normalisedTitle: entryData.normalisedTitle,
          companyId: entryData.companyId,
          companyName: typeof entryData.companyName === "string" ? entryData.companyName : undefined,
          applicationState: stateToSync,
          ...(typeof entryData.externalUrl === "string" && entryData.externalUrl.length > 0
            ? { externalUrl: entryData.externalUrl }
            : {}),
        } as Job;
      }
      if (!jobData) {
        // Try to get job data from other userState entries (saved, viewed, browsed)
        const savedItemKey = `savedJob#${jobId}` as const;
        const viewedItemKey = `viewedJob#${jobId}` as const;
        const browsedItemKey = `browsedJob#${jobId}` as const;
        
        const savedEntry = latestUserStateEntries[savedItemKey];
        const viewedEntry = latestUserStateEntries[viewedItemKey];
        const browsedEntry = latestUserStateEntries[browsedItemKey];
        
        const entry = savedEntry || viewedEntry || browsedEntry;
        if (entry?.data) {
          const entryData = entry.data as Record<string, unknown>;
          jobData = {
            id: jobId,
            title: entryData.title,
            normalisedTitle: entryData.normalisedTitle,
            companyId: entryData.companyId,
            companyName: typeof entryData.companyName === "string" ? entryData.companyName : undefined,
            applicationState: stateToSync,
            ...(typeof entryData.externalUrl === "string" && entryData.externalUrl.length > 0
              ? { externalUrl: entryData.externalUrl }
              : {}),
          } as Job;
        }
      }
      
      if (!jobData) {
        // If we still can't find job data, we can't sync
        console.warn('[syncApplicationStateToBackend] Cannot find job data:', {
          jobId,
        });
        return;
      }

      // Update job with the applicationState we want to sync
      jobData = { ...jobData, applicationState: stateToSync };

      // If we don't have the entry locally or it's a default entry, try to fetch it first to avoid conflicts
      if (!appliedEntry || (appliedEntry.version === 0 && appliedEntry.updatedAt === null)) {
        try {
          const fetchedEntry = await dispatch(
            userStateApi.endpoints.getUserStateItem.initiate(appliedItemKey)
          ).unwrap();
          // Only use the fetched entry if it actually exists on the server (updatedAt is not null)
          if (fetchedEntry.updatedAt !== null) {
            appliedEntry = fetchedEntry;
          }
        } catch (fetchError) {
          // If fetch fails, continue with version 0
          // This is expected for new items
        }
      }

      // Build payload with job data and applicationState
      // applicationState is stored as a field in the appliedJob# entry
      // Include appliedAt timestamp if this is a new applied job
      const payload = buildJobPayload(ensureJobId(jobData, jobId), {
        applicationState: stateToSync,
        ...(isApplied ? {} : { appliedAt: new Date().toISOString() }), // Add appliedAt if creating new applied job
      });
      
      try {
        await upsertJobEntry(
          appliedItemKey,
          payload,
          appliedEntry?.version ?? 0,
          {
            onConflict: (latest) => {
              // On conflict, merge the applicationState from server with our local state
              const serverJob = jobEntryToJob(latest);
              if (serverJob) {
                // Merge server state with local state, keeping our applicationState
                const mergedJob = { ...serverJob, applicationState: stateToSync };
                dispatch(removeAppliedJob(jobId));
                dispatch(addAppliedJob(mergedJob));
              }
              // Update the entry in Redux
              dispatch(setEntry({ itemKey: appliedItemKey, entry: latest }));
            },
            onSuccess: (result) => {
              // If this was a new applied job, add it to Redux
              // Note: setEntry is already called in upsertJobEntry, but we ensure jobData is in Redux jobs slice
              if (!isApplied) {
                dispatch(addAppliedJob(jobData));
              }
            },
            onError: () => {
              console.error('[syncApplicationStateToBackend] Failed to sync applicationState:', {
                jobId,
                applicationState: stateToSync,
              });
            },
          }
        );
        // Validate state was synced correctly
        const updatedState = store.getState();
        const allUpdatedJobs = [...updatedState.jobs.savedJobs, ...updatedState.jobs.viewedJobs, ...updatedState.jobs.appliedJobs];
        const updatedJob = allUpdatedJobs.find((j) => j.id === jobId);
        const hasCorrectState = updatedJob?.applicationState === stateToSync;
        return hasCorrectState;
      } catch (error) {
        console.error('[syncApplicationStateToBackend] Error syncing applicationState:', error);
        // Validate local state
        const updatedState = store.getState();
        const allUpdatedJobs = [...updatedState.jobs.savedJobs, ...updatedState.jobs.viewedJobs, ...updatedState.jobs.appliedJobs];
        const updatedJob = allUpdatedJobs.find((j) => j.id === jobId);
        const hasCorrectState = updatedJob?.applicationState === stateToSync;
        return hasCorrectState; // Return true if local state is correct
      }
    },
    [dispatch, isAuthenticated, upsertJobEntry, store]
  );

  const setAppliedJobState = useCallback(
    async (job: Job, applied: boolean): Promise<boolean> => {
      const jobId = normalizeJobId(job);
      if (!jobId) {
        return false;
      }

      const itemKey = `appliedJob#${jobId}` as const;
      let entry = userStateEntries[itemKey] as UserStateEntry | undefined;

      if (applied) {
        const alreadyApplied = appliedJobsRef.current.some((item) => item.id === jobId);
        if (alreadyApplied) {
          return true; // Already applied, consider it success
        }

        const enrichedJob = {
          ...ensureJobId(job, jobId),
          appliedAt: new Date().toISOString(),
        };

        // Always update local Redux state (which is persisted via redux-persist)
        dispatch(addAppliedJob(enrichedJob));

        // Validate local state was updated
        const state = store.getState();
        const isInAppliedJobs = state.jobs.appliedJobs.some((j) => j.id === jobId);
        if (!isInAppliedJobs) {
          console.error("[user-state] Failed to add applied job locally");
          return false;
        }

        // Only sync to remote if authenticated
        if (!isAuthenticated) {
          return true; // Success for local-only operation
        }

        // If we don't have the entry locally or it's a default entry, try to fetch it first to avoid conflicts
        if (!entry || (entry.version === 0 && entry.updatedAt === null)) {
          try {
            const fetchedEntry = await dispatch(
              userStateApi.endpoints.getUserStateItem.initiate(itemKey)
            ).unwrap();
            // Only use the fetched entry if it actually exists on the server (updatedAt is not null)
            if (fetchedEntry.updatedAt !== null) {
              entry = fetchedEntry;
            }
          } catch (fetchError) {
            // If fetch fails, continue with version 0
            // This is expected for new items
          }
        }

        try {
          await upsertJobEntry(
            itemKey,
            buildJobPayload(enrichedJob, { appliedAt: enrichedJob.appliedAt }),
            entry?.version ?? 0,
            {
              onConflict: (latest) => {
                const serverJob = jobEntryToJob(latest);
                if (serverJob) {
                  dispatch(removeAppliedJob(jobId));
                  dispatch(addAppliedJob(serverJob));
                }
              },
              onError: () => {
                dispatch(removeAppliedJob(jobId));
              },
              onSuccess: () => {
                // Success - job is synced
              },
            }
          );
          // Validate job is still in applied jobs after sync
          const updatedState = store.getState();
          const stillApplied = updatedState.jobs.appliedJobs.some((j) => j.id === jobId);
          return stillApplied;
        } catch (error) {
          // If upsertJobEntry throws or fails, validate local state
          const updatedState = store.getState();
          const stillApplied = updatedState.jobs.appliedJobs.some((j) => j.id === jobId);
          return stillApplied; // Return true if still in local state, false otherwise
        }
      } else {
        const existing = appliedJobsRef.current.find((item) => item.id === jobId);
        if (!existing) {
          return true; // Not applied, consider removal success
        }

        // Always update local Redux state (which is persisted via redux-persist)
        dispatch(removeAppliedJob(jobId));

        // Validate local state was updated
        const state = store.getState();
        const isRemoved = !state.jobs.appliedJobs.some((j) => j.id === jobId);
        if (!isRemoved) {
          console.error("[user-state] Failed to remove applied job locally");
          return false;
        }

        // Only sync to remote if authenticated
        if (!isAuthenticated) {
          return true; // Success for local-only operation
        }

        try {
          await deleteUserStateItem({
            itemKey,
            version: entry?.version,
          }).unwrap();
          // Validate job is still removed after remote delete
          const updatedState = store.getState();
          const stillRemoved = !updatedState.jobs.appliedJobs.some((j) => j.id === jobId);
          return stillRemoved;
        } catch (rawError) {
          const conflict = extractConflictEntry(rawError);
          if (conflict) {
            const restored = jobEntryToJob(conflict);
            if (restored) {
              dispatch(addAppliedJob(restored));
              // Validate job was restored
              const updatedState = store.getState();
              const wasRestored = updatedState.jobs.appliedJobs.some((j) => j.id === jobId);
              return !wasRestored; // Return false if job was restored (removal failed)
            }
          }
          dispatch(addAppliedJob(existing));
          return false; // Failed to remove
        }
      }
    },
    [deleteUserStateItem, dispatch, store, isAuthenticated, upsertJobEntry, userStateEntries]
  );

  const addExternalAppliedJob = useCallback(
    async (
      rawUrl: string,
      optionalTitle?: string
    ): Promise<
      | { success: true; jobId: string; jobTitle: string }
      | { success: false; reason: ExternalJobUrlNormalizeFailureReason | "sync_failed" }
    > => {
      const normalized = normalizeExternalJobUrl(rawUrl);
      if (!normalized.ok) {
        return { success: false, reason: normalized.reason };
      }
      const id = createExternalAppliedJobId();
      const trimmedTitle = optionalTitle?.trim();
      const job: Job = {
        id,
        ...(trimmedTitle ? { title: trimmedTitle, normalisedTitle: trimmedTitle } : {}),
        externalUrl: normalized.url,
        applicationState: null,
      };
      const ok = await setAppliedJobState(job, true);
      return ok ? { success: true, jobId: id, jobTitle: trimmedTitle || '' } : { success: false, reason: "sync_failed" };
    },
    [setAppliedJobState]
  );

  const syncJobIsHiddenToBackend = useCallback(
    async (jobId: string): Promise<void> => {
      if (!jobId || !isAuthenticated) return;
      const state = store.getState();
      const ref = resolveTrackedJobRef(jobId, state);
      if (!ref) return;

      const { itemKey, job } = ref;
      let entry = userStateEntries[itemKey] as UserStateEntry | undefined;

      if (!entry || (entry.version === 0 && entry.updatedAt === null)) {
        try {
          const fetchedEntry = await dispatch(
            userStateApi.endpoints.getUserStateItem.initiate(itemKey)
          ).unwrap();
          if (fetchedEntry.updatedAt !== null) {
            entry = fetchedEntry;
          }
        } catch {
          // expected for new keys
        }
      }

      try {
        await upsertJobEntry(
          itemKey,
          buildJobPayload(ensureJobId(job, jobId), timestampExtrasFromJob(job)),
          entry?.version ?? 0,
          {
            onConflict: (latest) => {
              const serverJob = jobEntryToJob(latest);
              if (!serverJob) return;
              if (itemKey.startsWith("appliedJob#")) {
                dispatch(removeAppliedJob(jobId));
                dispatch(addAppliedJob(serverJob));
              } else if (itemKey.startsWith("savedJob#")) {
                dispatch(removeSavedJob(jobId));
                dispatch(addSavedJob(serverJob));
              } else {
                dispatch(removeViewedJob(jobId));
                dispatch(addViewedJob(serverJob));
              }
              dispatch(setEntry({ itemKey, entry: latest }));
            },
            onError: () => {},
          },
        );
      } catch {
        // non-fatal
      }
    },
    [dispatch, isAuthenticated, store, upsertJobEntry, userStateEntries],
  );

  /**
   * Shared tail of every "edit one field on a tracked job" mutation (notes, tags, checklist, ...):
   * fetch the entry if we don't have a real version yet, then upsert with `patch` layered onto the
   * job's other stored fields. On a version conflict, the field we just wrote is ours to keep —
   * only the rest of the entry (stage, hidden, etc.) reconciles from the server, via `mergeField`.
   */
  const syncJobFieldToBackend = useCallback(
    async (jobId: string, patch: Record<string, unknown>, mergeField: (serverJob: Job) => Job): Promise<void> => {
      if (!isAuthenticated) return;
      const state = store.getState();
      const ref = resolveTrackedJobRef(jobId, state);
      if (!ref) return;

      const { itemKey, job } = ref;
      let entry = userStateEntries[itemKey] as UserStateEntry | undefined;

      if (!entry || (entry.version === 0 && entry.updatedAt === null)) {
        try {
          const fetchedEntry = await dispatch(
            userStateApi.endpoints.getUserStateItem.initiate(itemKey)
          ).unwrap();
          if (fetchedEntry.updatedAt !== null) {
            entry = fetchedEntry;
          }
        } catch {
          // expected for new keys
        }
      }

      try {
        await upsertJobEntry(
          itemKey,
          buildJobPayload({ ...ensureJobId(job, jobId), ...patch }, timestampExtrasFromJob(job)),
          entry?.version ?? 0,
          {
            onConflict: (latest) => {
              const serverJob = jobEntryToJob(latest);
              if (!serverJob) return;
              const merged = mergeField(serverJob);
              if (itemKey.startsWith("appliedJob#")) {
                dispatch(removeAppliedJob(jobId));
                dispatch(addAppliedJob(merged));
              } else if (itemKey.startsWith("savedJob#")) {
                dispatch(removeSavedJob(jobId));
                dispatch(addSavedJob(merged));
              } else {
                dispatch(removeViewedJob(jobId));
                dispatch(addViewedJob(merged));
              }
              dispatch(setEntry({ itemKey, entry: latest }));
            },
            onError: () => {},
          },
        );
      } catch {
        // non-fatal — field stays in local state, retried on the next edit
      }
    },
    [dispatch, isAuthenticated, store, upsertJobEntry, userStateEntries],
  );

  const setJobNotes = useCallback(
    async (jobId: string, notes: string): Promise<void> => {
      if (!jobId) return;
      // Guest-first: always land locally, same as every other tracked-job mutation.
      dispatch(updateJobNotes({ jobId, notes }));
      await syncJobFieldToBackend(jobId, { notes }, (serverJob) => ({ ...serverJob, notes }));
    },
    [dispatch, syncJobFieldToBackend],
  );

  const setJobTags = useCallback(
    async (jobId: string, tags: string[]): Promise<void> => {
      if (!jobId) return;
      dispatch(updateJobTags({ jobId, tags }));
      await syncJobFieldToBackend(jobId, { tags }, (serverJob) => ({ ...serverJob, tags }));
    },
    [dispatch, syncJobFieldToBackend],
  );

  const setJobChecklist = useCallback(
    async (jobId: string, checklist: ChecklistItem[]): Promise<void> => {
      if (!jobId) return;
      dispatch(updateJobChecklist({ jobId, checklist }));
      await syncJobFieldToBackend(jobId, { checklist }, (serverJob) => ({ ...serverJob, checklist }));
    },
    [dispatch, syncJobFieldToBackend],
  );

  return {
    recordViewedJob,
    recordBrowsedJob,
    removeViewedJob: removeViewedJobRemote,
    setAppliedJobState,
    syncApplicationStateToBackend,
    syncJobIsHiddenToBackend,
    addExternalAppliedJob,
    setJobNotes,
    setJobTags,
    setJobChecklist,
  };
}

